import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, RefreshCw, Calendar, Package, ArrowUp, ArrowDown, 
  X, Search, Download, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as fobSectionApi from '@/services/fobSectionApi';

const FOBSectionReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('all');
  const [operations, setOperations] = useState([]);
  const [viewMode, setViewMode] = useState('bundles');
  const [exporting, setExporting] = useState(false);
  
  // Drill-down state
  const [drillDown, setDrillDown] = useState({ open: false, date: '', type: '', bundles: [], loading: false });

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const [reportData, opsData] = await Promise.all([
        fobSectionApi.getFobEnhancedSummary({
          start_date: startDate,
          end_date: endDate,
          search: searchQuery || undefined,
          operation: selectedOperation === 'all' ? undefined : selectedOperation,
          view_mode: viewMode
        }),
        fobSectionApi.getFobOperations()
      ]);
      setReport(reportData.report || []);
      setOperations(opsData.operations || []);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, searchQuery, selectedOperation, viewMode]);

  useEffect(() => {
    if (startDate && endDate) fetchReport();
  }, [fetchReport, startDate, endDate]);

  // Auto-switch view mode based on selected operation
  // Bundle Receipt should use 'bundles' view, all other operations should use 'usns' view
  useEffect(() => {
    if (selectedOperation === 'all') {
      // Default to bundles view when showing all operations
      setViewMode('bundles');
    } else if (selectedOperation === 'Bundle Receipt') {
      setViewMode('bundles');
    } else {
      // All other operations (Sort No USN Tagging, Assortment, Packing, Internal Transfer) use USNs
      setViewMode('usns');
    }
  }, [selectedOperation]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedOperation('all');
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const data = await fobSectionApi.getFobTransactions({
        start_date: startDate,
        end_date: endDate,
        search: searchQuery || undefined,
        operation: selectedOperation === 'all' ? undefined : selectedOperation,
        view_mode: viewMode
      });
      const transactions = data.transactions || [];
      if (transactions.length === 0) {
        toast.warning('No data to export');
        return;
      }
      const headers = ['Date', 'Bundle Barcode', 'Operation', 'Inward', 'Outward', 'USN Count'];
      const csvRows = [headers.join(',')];
      transactions.forEach(txn => csvRows.push([txn.date, txn.bundle_barcode, txn.operation, txn.inward, txn.outward, txn.usn_count].join(',')));
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fob_section_report_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${transactions.length} transactions`);
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleDrillDown = async (date, type) => {
    try {
      setDrillDown({ open: true, date, type, bundles: [], loading: true });
      const params = {};
      if (selectedOperation && selectedOperation !== 'all') params.operation = selectedOperation;
      if (viewMode) params.view_mode = viewMode;
      const data = await fobSectionApi.getFobDrillDownData(date, type, params);
      setDrillDown(prev => ({ ...prev, bundles: data.bundles || [], loading: false }));
    } catch (error) {
      toast.error('Failed to fetch details');
      setDrillDown(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  
  const getTypeLabel = (type) => {
    const labels = {
      opening: 'Opening Balance',
      inward: 'Inward',
      outward: 'Outward',
      closing: 'Closing Balance'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    if (type === 'inward') return <ArrowDown className="w-4 h-4 text-green-600" />;
    if (type === 'outward') return <ArrowUp className="w-4 h-4 text-red-600" />;
    return <Package className="w-4 h-4 text-blue-600" />;
  };

  // Calculate totals
  const totalInward = report.reduce((sum, r) => sum + (r.inward || 0), 0);
  const totalOutward = report.reduce((sum, r) => sum + (r.outward || 0), 0);
  const latestClosing = report.length > 0 ? report[report.length - 1].closing : 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div><h1 className="text-xl font-bold font-display">Reports</h1><p className="text-sm text-[#E8DCC8]">FOB Section</p></div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport} className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader className="pb-4"><CardTitle className="flex items-center gap-2 text-lg"><Filter className="w-5 h-5" />Filters</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">Search (Bundle/USN Code)</Label>
                <div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-input" /></div>
              </div>
              <div><Label className="text-sm font-medium mb-1.5 block">From Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date" /></div>
              <div><Label className="text-sm font-medium mb-1.5 block">To Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date" /></div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Operation</Label>
                <Select value={selectedOperation} onValueChange={setSelectedOperation}><SelectTrigger data-testid="operation-filter"><SelectValue placeholder="All" /></SelectTrigger><SelectContent><SelectItem value="all">All Operations</SelectItem>{operations.map(op => (<SelectItem key={op} value={op}>{op}</SelectItem>))}</SelectContent></Select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5DFD0]">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">View Mode:</Label>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button onClick={() => setViewMode('bundles')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${viewMode === 'bundles' ? 'bg-[#2D5F4F] text-white' : 'text-gray-600'}`} data-testid="toggle-bundles"><Package className="w-4 h-4 inline mr-1.5" />Bundles</button>
                  <button onClick={() => setViewMode('usns')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${viewMode === 'usns' ? 'bg-[#2D5F4F] text-white' : 'text-gray-600'}`} data-testid="toggle-usns"><BarChart3 className="w-4 h-4 inline mr-1.5" />USNs</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleClearFilters} data-testid="clear-filters-btn"><X className="w-4 h-4 mr-1" />Clear</Button>
                <Button variant="outline" size="sm" onClick={fetchReport} className="bg-[#2D5F4F] text-white" data-testid="apply-filters-btn"><Search className="w-4 h-4 mr-1" />Apply</Button>
                <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exporting} className="border-green-600 text-green-600" data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" />{exporting ? 'Exporting...' : 'Export CSV'}</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Current Stock</p><p className="text-2xl font-bold text-[#1A1F2E]">{latestClosing}</p><p className="text-xs text-gray-500">{viewMode === 'usns' ? 'USNs' : 'Bundles'}</p></div><Package className="w-8 h-8 text-blue-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Total Inward</p><p className="text-2xl font-bold text-green-600">+{totalInward}</p></div><ArrowDown className="w-8 h-8 text-green-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Total Outward</p><p className="text-2xl font-bold text-red-600">-{totalOutward}</p></div><ArrowUp className="w-8 h-8 text-red-500" /></div></CardContent></Card>
          <Card className="border-[#E5DFD0]"><CardContent className="py-4"><div className="flex items-center justify-between"><div><p className="text-xs text-[#5A5F6E]">Operations</p><p className="text-2xl font-bold text-[#1A1F2E]">{operations.length}</p></div><BarChart3 className="w-8 h-8 text-purple-500" /></div></CardContent></Card>
        </div>

        {loading ? (<div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" /></div>
        ) : report.length === 0 ? (<Card className="border-[#E5DFD0]"><CardContent className="py-12 text-center"><Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" /><h3 className="text-lg font-semibold">No Data</h3><p className="text-[#5A5F6E]">No report data available for this period</p></CardContent></Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" />Day Wise Transaction Report<Badge variant="outline" className="ml-2">{viewMode === 'usns' ? 'USN Count' : 'Bundle Count'}</Badge></CardTitle>
              <p className="text-sm text-[#5A5F6E]">Click on any value to see details.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-gray-50"><TableHead className="font-semibold">Date</TableHead><TableHead className="font-semibold text-center">Opening</TableHead><TableHead className="font-semibold text-center">Inward (+)</TableHead><TableHead className="font-semibold text-center">Outward (-)</TableHead><TableHead className="font-semibold text-center">Closing</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.slice().reverse().map((row) => (
                      <TableRow key={row.date} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{formatDate(row.date)}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDrillDown(row.date, 'opening')}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`drill-down-opening-${row.date}`}
                          >
                            {row.opening}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDrillDown(row.date, 'inward')}
                            className={`${row.inward > 0 ? 'text-green-600 hover:text-green-800 hover:bg-green-50' : 'text-gray-400'}`}
                            disabled={row.inward === 0}
                            data-testid={`drill-down-inward-${row.date}`}
                          >
                            {row.inward > 0 ? `+${row.inward}` : '0'}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDrillDown(row.date, 'outward')}
                            className={`${row.outward > 0 ? 'text-red-600 hover:text-red-800 hover:bg-red-50' : 'text-gray-400'}`}
                            disabled={row.outward === 0}
                            data-testid={`drill-down-outward-${row.date}`}
                          >
                            {row.outward > 0 ? `-${row.outward}` : '0'}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDrillDown(row.date, 'closing')}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-semibold"
                            data-testid={`drill-down-closing-${row.date}`}
                          >
                            {row.closing}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Drill-down Dialog */}
      <Dialog open={drillDown.open} onOpenChange={(open) => !open && setDrillDown({ open: false, date: '', type: '', bundles: [], loading: false })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getTypeIcon(drillDown.type)}
              {getTypeLabel(drillDown.type)} - {drillDown.date && formatDate(drillDown.date)}
              <Badge className="ml-2">{drillDown.bundles.length} bundles</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {drillDown.loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-[#2D5F4F]" />
            </div>
          ) : drillDown.bundles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No bundles found for this {drillDown.type}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">#</TableHead>
                    <TableHead className="text-xs">Bundle Barcode</TableHead>
                    <TableHead className="text-xs">Operation</TableHead>
                    <TableHead className="text-xs">Document</TableHead>
                    {drillDown.type === 'outward' && (
                      <TableHead className="text-xs">To Section</TableHead>
                    )}
                    <TableHead className="text-xs">USN Count</TableHead>
                    <TableHead className="text-xs">Staff</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDown.bundles.slice(0, 100).map((bundle, idx) => (
                    <TableRow key={`${bundle.bundle_barcode}-${idx}`}>
                      <TableCell className="text-xs">{idx + 1}</TableCell>
                      <TableCell className="font-mono text-xs">{bundle.bundle_barcode || '-'}</TableCell>
                      <TableCell className="text-xs">{bundle.operation || '-'}</TableCell>
                      <TableCell className="text-xs">{bundle.document_barcode || '-'}</TableCell>
                      {drillDown.type === 'outward' && (
                        <TableCell className="text-xs">{bundle.to_section || '-'}</TableCell>
                      )}
                      <TableCell className="text-xs">{bundle.usn_count || '-'}</TableCell>
                      <TableCell className="text-xs">{bundle.staff_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {drillDown.bundles.length > 100 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  Showing first 100 of {drillDown.bundles.length} bundles
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FOBSectionReport;
